/**
 * Week Key Utilities
 * 
 * Provides functions for working with ISO week keys (format: 'YYYY-WNN').
 * ISO weeks start on Monday and are numbered 1-52/53 based on the year.
 */

export type WeekKey = string; // e.g. '2025-W34'

/**
 * Calculate ISO week number and year from a Date
 * 
 * ISO weeks start on Monday. Week 1 is the first week with a Thursday.
 * Uses the local calendar date (y/m/d) at noon UTC to avoid midnight/DST edge cases.
 * 
 * @param d Date object to calculate ISO week for
 * @returns Object with year and week number
 * 
 * @internal
 */
function isoWeek(d: Date): { year: number; week: number } {
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  const date = new Date(Date.UTC(y, m, day, 12, 0, 0));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: date.getUTCFullYear(), week };
}

/**
 * Get current ISO week key (never returns a future week).
 *
 * Uses the local calendar date (today). If the computed week's Monday is after
 * today, returns the previous week so the invariant "current week is never
 * future" holds for UI, save attribution, and forward-fill.
 *
 * @param base Optional Date to use as base (defaults to start of today, local)
 * @returns Week key in format 'YYYY-WNN', guaranteed to be current or past
 */
export function getCurrentISOWeekKey(base?: Date): WeekKey {
  const now = base ?? new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const { year, week } = isoWeek(todayStart);
  const raw = `${year}-W${String(week).padStart(2, '0')}`;
  const weekStart = getWeekStartDateFromKey(raw);
  if (!weekStart) {
    return getPreviousWeekKey(raw);
  }
  const weekStartLocal = new Date(
    weekStart.getUTCFullYear(),
    weekStart.getUTCMonth(),
    weekStart.getUTCDate()
  );
  if (weekStartLocal > todayStart) {
    try {
      const w = typeof window !== 'undefined' ? window : null;
      if (w && typeof w === 'object') {
        const m = (w as Window & { __marlo?: { currentWeekClampCount?: number; lastClampedWeekKey?: string } }).__marlo;
        if (m) {
          m.currentWeekClampCount = (m.currentWeekClampCount ?? 0) + 1;
          m.lastClampedWeekKey = raw;
        } else {
          (w as Window & { __marlo?: { currentWeekClampCount?: number; lastClampedWeekKey?: string } }).__marlo = {
            currentWeekClampCount: 1,
            lastClampedWeekKey: raw,
          };
        }
      }
    } catch {
      // no-op
    }
    return getPreviousWeekKey(raw);
  }
  return raw;
}

/**
 * Get current week key (backward compatibility wrapper)
 * 
 * @returns Current week key in format 'YYYY-WNN'
 * 
 * @deprecated Use getCurrentISOWeekKey() instead
 */
export function getCurrentWeekKey(): WeekKey {
  return getCurrentISOWeekKey();
}

/**
 * Convert a Date to a WeekKey
 * 
 * @param date Date object to convert
 * @returns Week key in format 'YYYY-WNN'
 * 
 * @example
 * getWeekKeyFromDate(new Date('2025-10-16')) // '2025-W42'
 */
export function getWeekKeyFromDate(date: Date): WeekKey {
  return getCurrentISOWeekKey(date);
}

/**
 * Get the next week key
 * 
 * @param currentKey Current week key in format 'YYYY-WNN'
 * @returns Next week key, rolling over to next year if needed
 * 
 * @example
 * getNextWeekKey('2025-W52') // '2026-W01'
 * getNextWeekKey('2025-W40') // '2025-W41'
 */
export const getNextWeekKey = (currentKey: string): string => {
  const [year, week] = currentKey.split('-W').map(Number);
  const nextWeek = week + 1;
  // If nextWeek exceeds 52, roll over to next year
  if (nextWeek > 52) return `${year + 1}-W01`;
  return `${year}-W${String(nextWeek).padStart(2, '0')}`;
};

/**
 * Get the previous week key
 * 
 * @param currentKey Current week key in format 'YYYY-WNN'
 * @returns Previous week key, rolling back to previous year if needed
 * 
 * @example
 * getPreviousWeekKey('2025-W01') // '2024-W52' (year rollover example)
 * getPreviousWeekKey('2025-W40') // '2025-W39'
 */
export const getPreviousWeekKey = (currentKey: string): string => {
  // ✅ FIX: Use date math instead of hardcoding W52 to handle ISO week 53 correctly
  // Subtract 7 days from the week start date and recompute ISO week
  const currentDate = getWeekStartDateFromKey(currentKey);
  if (!currentDate) {
    // Fallback to simple calculation if date parsing fails
    const [year, week] = currentKey.split('-W').map(Number);
    const prevWeek = week - 1;
    if (prevWeek <= 0) return `${year - 1}-W52`;
    return `${year}-W${String(prevWeek).padStart(2, '0')}`;
  }

  // ✅ CRITICAL: Use isoWeek() directly — do NOT call getCurrentISOWeekKey(previousDate)
  // getCurrentISOWeekKey does "never future" clamp and can call getPreviousWeekKey again → infinite recursion
  const previousDate = new Date(currentDate);
  previousDate.setUTCDate(currentDate.getUTCDate() - 7);
  const { year, week } = isoWeek(previousDate);
  return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Parse a week key string into year and week number
 * 
 * Null-safe parser that validates the week key format.
 * 
 * @param maybeKey Week key in format 'YYYY-WNN', or null/undefined
 * @returns Object with year and week number, or null if invalid
 * 
 * @example
 * parseWeekKey('2025-W40') // { year: 2025, week: 40 }
 * parseWeekKey('invalid') // null
 * parseWeekKey(null) // null
 */
export function parseWeekKey(maybeKey?: string | null): { year: number; week: number } | null {
  if (!maybeKey || typeof maybeKey !== 'string') return null;
  const m = maybeKey.match(/^(\d{4})-W(\d{1,2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  if (!Number.isFinite(year) || !Number.isFinite(week)) return null;
  return { year, week };
}

/**
 * Get the start date (Monday) of a week from a week key
 * 
 * ISO weeks start on Monday. This function calculates the Monday
 * of the specified week using UTC to avoid timezone issues.
 * 
 * @param maybeKey Week key in format 'YYYY-WNN', or null/undefined
 * @returns Date object for Monday of the week (UTC), or null if invalid
 * 
 * @example
 * getWeekStartDateFromKey('2025-W40') // Date for Monday of week 40, 2025
 */
export function getWeekStartDateFromKey(maybeKey?: string | null): Date | null {
  const parsed = parseWeekKey(maybeKey);
  if (!parsed) return null;
  const { year, week } = parsed;
  
  // ✅ FIX: Use iterative approach to find the Monday that produces the correct week key
  // This ensures perfect consistency with isoWeek() algorithm
  // Start from Jan 1 and search through Mondays until we find the one that matches
  
  // Start from the first Monday of the year (or earlier if week 1 starts in previous year)
  let searchDate = new Date(Date.UTC(year, 0, 1));
  const firstDay = searchDate.getUTCDay() || 7;
  // Find the first Monday (or go back to previous Monday if Jan 1 is not Monday)
  const daysToFirstMonday = firstDay === 1 ? 0 : firstDay === 7 ? 6 : firstDay - 1;
  searchDate.setUTCDate(searchDate.getUTCDate() - daysToFirstMonday);
  
  // If week 1 might start in previous year, go back a week
  if (week === 1) {
    searchDate.setUTCDate(searchDate.getUTCDate() - 7);
  }
  
  // Search through up to 60 Mondays (covers full year + buffer)
  for (let i = 0; i < 60; i++) {
    // ✅ CRITICAL FIX: Use isoWeek() directly to avoid infinite recursion
    // DO NOT call getWeekKeyFromDate() here as it creates a circular dependency:
    // getWeekStartDateFromKey → getWeekKeyFromDate → getCurrentISOWeekKey → getWeekStartDateFromKey → loop
    const { year: testYear, week: testWeek } = isoWeek(searchDate);
    const testWeekKey = `${testYear}-W${String(testWeek).padStart(2, '0')}`;
    if (testWeekKey === maybeKey) {
      return new Date(searchDate); // Return a copy
    }
    // Move to next Monday
    searchDate.setUTCDate(searchDate.getUTCDate() + 7);
  }
  
  // If not found, return null (should never happen with valid week key)
  return null;
}

/**
 * Get the end date (Sunday) of a week from a week key
 * 
 * ISO weeks end on Sunday. This function calculates the Sunday
 * of the specified week (6 days after the Monday start).
 * 
 * @param maybeKey Week key in format 'YYYY-WNN', or null/undefined
 * @returns Date object for Sunday of the week (UTC), or null if invalid
 * 
 * @example
 * getWeekEndDateFromKey('2025-W40') // Date for Sunday of week 40, 2025
 */
export function getWeekEndDateFromKey(maybeKey?: string | null): Date | null {
  const startDate = getWeekStartDateFromKey(maybeKey);
  if (!startDate) return null;
  const endDate = new Date(startDate);
  endDate.setUTCDate(startDate.getUTCDate() + 6); // Add 6 days to get Sunday
  return endDate;
}

/**
 * Validate if a week key is in the correct format
 * 
 * @param maybeKey Week key to validate, or null/undefined
 * @returns true if valid format 'YYYY-WNN', false otherwise
 * 
 * @example
 * isValidWeekKey('2025-W40') // true
 * isValidWeekKey('2025-40') // false
 * isValidWeekKey(null) // false
 */
export function isValidWeekKey(maybeKey?: string | null): boolean {
  return parseWeekKey(maybeKey) !== null;
}

/**
 * Format week key for detailed display
 * 
 * Returns a human-readable label showing the week start date.
 * Use this for headers, titles, or detailed week information.
 * 
 * @param maybeKey Week key in format 'YYYY-WNN' (e.g., '2025-W40'), or null/undefined
 * @returns Formatted string like "Week of 09/29/2025", or "Current Week" if invalid
 * 
 * @example
 * formatWeekLabel('2025-W40') // "Week of 09/29/2025"
 * formatWeekLabel(null) // "Current Week"
 */
export function formatWeekLabel(maybeKey?: string | null): string {
  const start = getWeekStartDateFromKey(maybeKey);
  if (!start) return 'Current Week';
  const m = (n: number) => String(n).padStart(2, '0');
  return `Week of ${m(start.getUTCMonth() + 1)}/${m(start.getUTCDate())}/${start.getUTCFullYear()}`;
}

/**
 * Format week key for compact display (navigation, dropdowns, buttons)
 * 
 * Returns a short date format suitable for UI elements where space is limited.
 * Use this for navigation bars, calendar dropdowns, week selectors, and compact displays.
 * 
 * @param maybeKey Week key in format 'YYYY-WNN' (e.g., '2025-W40'), or null/undefined
 * @returns Formatted string like "9/29/25" (M/D/YY), or "Current Week" if invalid
 * 
 * @example
 * formatWeekKey('2025-W40') // "9/29/25"
 * formatWeekKey('2025-W01') // "12/30/24" (if week 1 starts in previous year)
 * formatWeekKey(null) // "Current Week"
 * 
 * @note This function replaces the previous formatCalendarWeek() which was identical.
 *       All week formatting now uses either formatWeekLabel() (detailed) or formatWeekKey() (compact).
 */
export function formatWeekKey(maybeKey?: string | null): string {
  const start = getWeekStartDateFromKey(maybeKey);
  if (!start) return 'Current Week';
  const month = start.getUTCMonth() + 1;
  const day = start.getUTCDate();
  const year = start.getUTCFullYear().toString().slice(-2); // Get last 2 digits
  return `${month}/${day}/${year}`;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Get month-year label for grouping (e.g. "December 2025").
 * Uses the week's Monday (UTC) so the week starting 12/1/25 is always "December 2025",
 * not "November 2025" in timezones where 12/1 00:00 UTC is still Nov 30 local.
 */
export function getMonthYearLabelForWeekKey(maybeKey?: string | null): string {
  const start = getWeekStartDateFromKey(maybeKey);
  if (!start) return 'Other';
  const month = start.getUTCMonth();
  const year = start.getUTCFullYear();
  return `${MONTH_NAMES[month]} ${year}`;
} 